import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Shield, ArrowLeft, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FORMULES = {
  'domaine-complet': 'Domaine complet + Salle des fetes',
  'gite-complet': 'Gite complet (2 appartements)',
  'gite-salle': 'Gite + Salle des fetes',
  'gite-seul': 'Gite seul',
  'appartement-1': "L'Appartement Terrasse",
  'appartement-2': "L'Appartement Panorama",
};

function formatEuro(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(n || 0);
}

export default function PaymentBalance() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email') || '';
    axios.get(`${BACKEND_URL}/api/payments/reservation/${reservationId}?email=${encodeURIComponent(email)}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { setError('Reservation introuvable ou email invalide.'); setLoading(false); });
  }, [reservationId]);

  const handlePay = async () => {
    setPaying(true);
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        reservation_id: reservationId,
        origin_url: window.location.origin,
        payment_type: 'balance',
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la creation du paiement.');
      setPaying(false);
    }
  };

  const reservation = data?.reservation;
  const canPay = data?.can_pay_balance;
  const balance = data?.balance_due || 0;
  const balanceDueDate = data?.balance_due_date || reservation?.balance_due_date;

  const daysUntilDue = useMemo(() => {
    if (!balanceDueDate) return null;
    const due = new Date(balanceDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  }, [balanceDueDate]);

  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
  const isUrgent = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 14;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <p className="text-[#595959]">Chargement...</p>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-[#595959] text-lg mb-4">{error || 'Reservation introuvable.'}</p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-[#2C3E30] text-[#F2F0E9]">Retour</Button>
        </div>
      </div>
    );
  }

  if (reservation.payment_status === 'paid') {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <CheckCircle className="w-16 h-16 text-[#8A9A5B] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-['Playfair_Display'] text-3xl text-[#2C3E30] mb-3">Deja paye !</h1>
          <p className="text-[#595959] mb-6">Cette reservation est entierement payee. Merci !</p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-[#2C3E30] text-[#F2F0E9]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour au site
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <button
          data-testid="balance-back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#595959] hover:text-[#2C3E30] mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Retour au site
        </button>

        <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)]">
          <div className="text-center mb-8">
            <CreditCard className="w-12 h-12 text-[#C4A962] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#2C3E30]">Paiement du solde</h1>
            <p className="text-[#595959] text-sm mt-1">Le Domaine du Pilat</p>
          </div>

          {/* Reservation summary */}
          <div className="bg-[#F5F5F0] rounded-xl p-5 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#595959]">Client</span>
              <span className="font-medium">{reservation.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#595959]">Formule</span>
              <span className="font-medium">{FORMULES[reservation.accommodation_type] || reservation.accommodation_type}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#595959]">Dates</span>
              <span className="font-medium">{reservation.arrival_date} → {reservation.departure_date}</span>
            </div>
            <hr className="border-[#E5E5E0]" />
            <div className="flex justify-between text-sm">
              <span className="text-[#595959]">Montant total</span>
              <span className="font-semibold">{formatEuro(reservation.amount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#595959]">Acompte verse (30%)</span>
              <span className="text-[#8A9A5B] font-semibold">-{formatEuro(reservation.deposit_amount)}</span>
            </div>
            <hr className="border-[#E5E5E0]" />
            <div className="flex justify-between">
              <span className="font-semibold text-[#2C3E30]">Solde a payer</span>
              <span className="font-bold text-xl text-[#C4A962]">{formatEuro(balance)}</span>
            </div>
            {balanceDueDate && (
              <div className={`flex items-center gap-2 text-xs mt-2 p-2 rounded-lg ${isOverdue ? 'bg-red-50 text-[#993333]' : isUrgent ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                {isOverdue ? <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> : <Clock className="w-3.5 h-3.5 flex-shrink-0" />}
                <span>
                  {isOverdue
                    ? `Date limite depassee (${balanceDueDate}). Risque d'annulation automatique.`
                    : isUrgent
                      ? `Urgent : solde a regler avant le ${balanceDueDate} (${daysUntilDue} jour${daysUntilDue > 1 ? 's' : ''} restant${daysUntilDue > 1 ? 's' : ''}).`
                      : `Solde a regler avant le ${balanceDueDate}.`
                  }
                </span>
              </div>
            )}
          </div>

          {canPay ? (
            <>
              <div className="flex items-center gap-2 text-xs text-[#595959] mb-4">
                <Shield className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} />
                <span>Paiement securise par Stripe - Chiffrement SSL</span>
              </div>

              {error && <p data-testid="balance-error" className="text-[#993333] text-sm mb-4">{error}</p>}

              <Button
                data-testid="pay-balance-btn"
                onClick={handlePay}
                disabled={paying}
                className="w-full rounded-full py-6 text-base bg-[#C4A962] text-white hover:bg-[#b5945a] transition-transform active:scale-95 font-medium"
                size="lg"
              >
                {paying ? 'Redirection...' : `Payer le solde de ${formatEuro(balance)}`}
              </Button>
            </>
          ) : (
            <p className="text-center text-[#595959] text-sm">
              Le paiement du solde n'est pas encore disponible pour cette reservation.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
